import { useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Archive, FlaskConical, KanbanSquare, List, Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { EDIT_ROLES } from '../utils/permissions'
import { useSampleOrderList, useSampleOrderStats, useTransitionSampleOrder } from '../api/queries'
import type { SampleOrderSummary, Stage } from '../api/types'
import { BOARD_STAGES, STAGE_LABELS, STAGE_VARIANT } from '../utils/stage-meta'
import { NewSampleOrderDrawer } from '../components/NewSampleOrderDrawer'
import { SampleOrdersKanban } from '../components/SampleOrdersKanban'

type ViewMode = 'table' | 'kanban'

const VIEW_OPTIONS = [
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
  { value: 'table' as const, label: 'Table', icon: List },
]

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SampleOrdersPage() {
  const navigate = useNavigate()
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState<ViewMode>('kanban')
  const [showArchived, setShowArchived] = useState(false)
  const [newOpen, setNewOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const canEdit = useHasAnyPermission(EDIT_ROLES)

  const openOrder = (id: string) =>
    navigate({ to: '/sample-orders/$orderId', params: { orderId: id } })

  const stats = useSampleOrderStats()
  const list = useSampleOrderList({
    ...(stageFilter === 'all' ? {} : { status: stageFilter }),
    archived: showArchived,
  })
  const transition = useTransitionSampleOrder()

  const items = list.data?.items ?? []
  const filtered = searchTerm.trim()
    ? items.filter((row) => {
        const needle = searchTerm.trim().toLowerCase()
        return (
          row.customer_name?.toLowerCase().includes(needle) ||
          row.prospect_name?.toLowerCase().includes(needle) ||
          row.order_number.toLowerCase().includes(needle) ||
          row.tracking_number?.toLowerCase().includes(needle)
        )
      })
    : items

  const columns = useColumns()

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Sample Orders"
        description="SPI's own commercial product samples — filed, tracked, and fulfilled through Odoo."
      >
        <div className="ml-auto flex items-center gap-3">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Open" value={String(stats.data?.open ?? 0)} />
          </MetricStrip>
          {canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
              <Plus size={14} className="mr-1.5" /> New order
            </Button>
          ) : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <SearchInput
          ref={searchInputRef}
          containerClassName="flex-1 max-w-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search customer, order #, tracking…"
        />
      </Toolbar>

      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            variant={showArchived ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
          >
            <Archive size={14} className="mr-1.5" />
            {showArchived ? 'Showing shipped & closed' : 'Shipped & closed'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FlaskConical}
            title="No sample orders here yet"
            description={
              stageFilter === 'all' && !showArchived
                ? 'Click “New order” to file an SPI sample order. Confirm creates a draft Odoo sales order for the warehouse to fulfill.'
                : 'No orders match the current filters.'
            }
          />
        ) : view === 'kanban' && !showArchived ? (
          <SampleOrdersKanban
            items={filtered}
            onSelect={(order) => openOrder(order.id)}
            onTransition={(order, target) => {
              transition.mutate(
                { orderId: order.id, targetStage: target },
                {
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'transition failed'
                    toast.error(msg, { duration: 5000 })
                  },
                },
              )
            }}
          />
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            loading={list.isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => openOrder(r.id)}
            moduleId="sample-orders"
            maxHeight="100%"
            columnWidths={{
              order_number: 110,
              customer: 200,
              stage: 150,
              owner: 180,
              need_by: 130,
              tracking: 160,
              created_at: 110,
            }}
            minTableWidth={1000}
            emptyState={
              <EmptyState
                variant="no-match"
                title="No matches"
                description="Try clearing the stage filter or your search."
              />
            }
          />
        )}
      </div>

      <NewSampleOrderDrawer open={newOpen} onOpenChange={setNewOpen} onCreated={openOrder} />
    </div>
  )
}

function useColumns(): ColumnDef<SampleOrderSummary, unknown>[] {
  return useMemo<ColumnDef<SampleOrderSummary, unknown>[]>(
    () => [
      {
        id: 'order_number',
        header: 'Order',
        accessorKey: 'order_number',
        cell: (ctx) => (
          <span className="text-role-data-primary">{ctx.row.original.order_number}</span>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        accessorFn: (r) => r.customer_name ?? r.prospect_name ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          return r.customer_name ?? r.prospect_name ?? <em className="text-tertiary">—</em>
        },
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => STAGE_LABELS[r.status],
        cell: (ctx) => {
          const r = ctx.row.original
          return (
            <div className="flex items-center gap-2">
              <Chip size="xs" variant={STAGE_VARIANT[r.status]} dot>
                {STAGE_LABELS[r.status]}
              </Chip>
              {r.status === 'blocked' && r.blocked_reason_label ? (
                <Chip size="xs" variant="neutral">
                  {r.blocked_reason_label}
                </Chip>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (r) => r.assigned_to ?? '',
        cell: (ctx) => (
          <span className="text-secondary">
            {ctx.row.original.assigned_to ?? <em className="text-tertiary">unassigned</em>}
          </span>
        ),
      },
      {
        id: 'need_by',
        header: 'Need by',
        accessorFn: (r) => r.need_by_date ?? '',
        cell: (ctx) => (
          <span className="text-tertiary">{formatDate(ctx.row.original.need_by_date)}</span>
        ),
      },
      {
        id: 'tracking',
        header: 'Tracking',
        accessorFn: (r) => r.tracking_number ?? '',
        cell: (ctx) => (
          <span className="text-secondary">{ctx.row.original.tracking_number ?? '—'}</span>
        ),
      },
      {
        id: 'created_at',
        header: 'Requested',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-tertiary">{formatDate(ctx.row.original.created_at)}</span>
        ),
      },
    ],
    [],
  )
}
